import { icon } from '@carvana/ds-styles';
import { AlertCircle } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import DefaultAvailabilityOptions from '@/common/helios/Modals/GeoModal/DefaultAvailabilityOptions';
import { useLocationContext } from '~/context/common/LocationContext';
import maybeErrored from '~/utilities/geolocation/maybeErrored';
import GeolocationForm from './GeolocationForm';
import {
  getSubmissionCallback,
  handleSelectOnFocus,
  handleSubmit
} from './LocationInputUtils';
import {
  Copy,
  LowerContentWrapper,
  OptionTitle,
  OptionWrapper
} from './ModalContent.styles';

type OwnProps = {
  AvailabilityOptions?: ReactNode;
};

export default function ModalContents({ AvailabilityOptions }: OwnProps) {
  const {
    isDeliveryAllowed,
    pickUpInfoToDisplay,
    zip,
    setGeolocation,
    onCloseHandler,
    errorState,
    city,
    state,
    trackEvent
  } = useLocationContext();
  const isExcludedLocation = !isDeliveryAllowed && !pickUpInfoToDisplay;
  const hasErrored = maybeErrored(errorState);

  const [inputValue, setInputValue] = useState(zip);

  const handleSetGeolocation = useCallback(() => {
    trackEvent('Geolocation - Changed Zip', {
      action: 'clicked',
      newZip: inputValue,
      previousZip: zip
    });

    return setGeolocation(inputValue);
  }, [inputValue, setGeolocation, trackEvent, zip]);

  const inputProps = useMemo(
    () => ({
      onBlur: () => {
        if (inputValue === zip || !inputValue) {
          setInputValue(zip);
        }
      },
      onFocus: handleSelectOnFocus,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.currentTarget.value);
      },
      value: inputValue,
      error: hasErrored ? 'Enter a valid ZIP code' : ''
    }),
    [hasErrored, inputValue, zip]
  );

  useEffect(() => {
    setInputValue(zip);
  }, [zip]);

  return (
    <div
      className="flex w-full flex-col gap-2xl bg-white"
      data-testid="geolocation-modal"
    >
      <GeolocationForm
        inputProps={inputProps}
        onSubmit={handleSubmit(
          handleSetGeolocation,
          inputValue,
          getSubmissionCallback(setInputValue, zip),
          onCloseHandler
        )}
      />

      <LowerContentWrapper>
        <OptionTitle data-testid="Modal Content Location">
          Options for {city}, {state}
        </OptionTitle>

        {isExcludedLocation ? (
          <OptionWrapper>
            <OptionTitle>
              <AlertCircle color={icon.warning} size={16} />
              Pickup and delivery
            </OptionTitle>
            <Copy>Unavailable in your location.</Copy>
          </OptionWrapper>
        ) : AvailabilityOptions ? (
          AvailabilityOptions
        ) : (
          <DefaultAvailabilityOptions />
        )}
      </LowerContentWrapper>
    </div>
  );
}
